import { Button, FieldError, Input, TextField } from "@heroui/react";
import { Icon } from "@iconify/react";
import { memo, useState } from "react";
import { StepHeading } from "@funnel-ui/primitives/StepHeading";
import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

type ResultsCharacter = {
  id: string;
  name: string;
  imageUrl: string;
};

type ResultsEmailCaptureProps = {
  headline?: string;
  description?: string;
  characters: ResultsCharacter[];
  ctaLabel?: string;
  trustText?: string;
  isSubmitting?: boolean;
  onSubmit?: (email: string) => void | Promise<void>;
  className?: string;
  testId?: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ResultsEmailCaptureImpl = ({
  headline = "Here are your matches",
  description = "Enter your email to unlock the full results.",
  characters,
  ctaLabel = "Show me the results",
  trustText = "We'll only email about your matches.",
  isSubmitting,
  onSubmit,
  className,
  testId,
}: ResultsEmailCaptureProps) => {
  const [email, setEmail] = useState("");
  const valid = EMAIL_REGEX.test(email.trim());
  const [error, setError] = useState<string | undefined>(undefined);

  const handleSubmit = (): void => {
    if (!valid) {
      setError("Please enter a valid email.");
      return;
    }
    setError(undefined);
    void onSubmit?.(email.trim());
  };

  return (
    <section data-testid={testId} className={`flex flex-col gap-5 ${className ?? ""}`}>
      <StepHeading title={headline} description={description} />

      <div className="grid grid-cols-3 gap-2">
        {characters.slice(0, 3).map((character) => (
          <div key={character.id} className="flex flex-col items-center gap-1.5">
            <div className="relative w-full overflow-hidden rounded-xl">
              <ResponsiveAsset
                src={character.imageUrl}
                alt=""
                loading="eager"
                className="aspect-[3/4] w-full object-cover"
              />
            </div>
            <span
              className="text-xs font-semibold"
              style={{ color: "hsl(var(--brand-bg-foreground))" }}
            >
              {character.name}
            </span>
          </div>
        ))}
      </div>

      <TextField
        value={email}
        onChange={(v: string) => {
          setEmail(v);
          if (error) setError(undefined);
        }}
        isInvalid={!!error}
      >
        <Input type="email" inputMode="email" autoComplete="email" placeholder="you@example.com" />
        {error && <FieldError>{error}</FieldError>}
      </TextField>

      <Button
        variant="primary"
        size="lg"
        fullWidth
        onPress={handleSubmit}
        isDisabled={!valid || isSubmitting}
        className="rounded-full"
      >
        {isSubmitting ? "Working..." : ctaLabel}
      </Button>

      <div
        className="flex items-center gap-2 text-xs"
        style={{ color: "hsl(var(--hero-fg-muted))" }}
      >
        <Icon icon="gravity-ui:lock" width={12} height={12} />
        <span>{trustText}</span>
      </div>
    </section>
  );
};

export const ResultsEmailCapture = memo(ResultsEmailCaptureImpl);

export default ResultsEmailCapture;
